<template>
  <section class="bg-white py-10">
    <div class="max-w-screen-3xl mx-auto px-0 sm:px-6 lg:px-8 2xl:px-22">
      <div class="px-4 sm:px-6 lg:px-8 2xl:px-10">
        <!-- Section Header -->
        <div v-if="showHeader" class="text-center mb-10">
          <h2
            class="text-3xl lg:text-[40px] font-semibold font-outfit text-gray-800 tracking-[-0.68px]"
          >
            <template v-if="title">{{ title }}</template>
            <template v-else> Nos formules Omra </template>
          </h2>
        </div>
      </div>

      <!-- Category Tabs -->
      <div class="px-4 sm:px-6 lg:px-8 2xl:px-10">
        <div v-if="showTabs" class="flex justify-center mb-12">
          <div
            ref="tabsEl"
            class="relative inline-flex bg-primary-light border-2 border-primary/50 rounded-[50px] p-1 backdrop-blur-[12px] overflow-hidden"
          >
            <div
              class="absolute top-1 bottom-1 left-0 rounded-[50px] bg-primary transition-all duration-300 ease-out transform-gpu will-change-transform"
              :style="indicatorStyle"
            ></div>
            <button
              v-for="category in categories"
              :key="category.value"
              :ref="setTabRef"
              @click="activeCategory = category.value"
              type="button"
              :aria-pressed="activeCategory === category.value"
              :class="[
                'relative z-10 flex-none w-auto px-5 py-3 rounded-[50px] text-base font-semibold font-outfit transition-colors duration-200 text-center whitespace-nowrap',
                activeCategory === category.value
                  ? 'text-white'
                  : 'text-gray-700 hover:text-[#8a6b22]',
              ]"
              :data-tab="category.value"
            >
              {{ category.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Package Cards Grid -->
      <div class="">
        <div class="relative">
          <!-- Navigation Arrows (Hidden on Mobile) -->
          <button
            v-if="canScrollLeft"
            @click="scrollLeft"
            class="hidden sm:flex group absolute -left-3 lg:-left-16 top-[160px] lg:top-[190.5px] z-10 w-[43px] h-[43px] rounded-full bg-white border-primary/30 border-2 hover:bg-primary shadow-sm items-center justify-center hover:border-[#E8D2A3] transition-colors"
            aria-label="Précédent"
          >
            <IconRight
              :size="16"
              class="text-primary rotate-180 transition-colors group-hover:text-white"
            />
          </button>

          <button
            v-if="canScrollRight"
            @click="scrollRight"
            class="hidden sm:flex group absolute -right-3 lg:-right-16 top-[160px] lg:top-[190.5px] z-10 w-[43px] h-[43px] rounded-full bg-white border-primary/30 border-2 hover:border-[#E8D2A3] hover:bg-primary shadow-sm items-center justify-center transition-colors"
            aria-label="Suivant"
          >
            <IconRight
              :size="16"
              class="text-primary transition-colors group-hover:text-white"
            />
          </button>

          <!-- Cards Container -->
          <div
            ref="scrollContainer"
            :class="[
              'flex gap-6 sm:gap-10 overflow-x-auto scrollbar-hide scroll-smooth pb-0 snap-x snap-mandatory',
              'px-4 sm:px-0 scroll-pl-4 scroll-pr-4 sm:scroll-pl-0 sm:scroll-pr-0',
              { 'cards-animate': animateCards },
            ]"
            @scroll="updateScrollState"
          >
            <!-- Loading State -->
            <template v-if="loading">
              <div
                v-for="i in 3"
                :key="i"
                class="flex-shrink-0 w-[85%] sm:w-full md:w-[calc(50%-20px)] lg:w-[calc(33.333%-27px)] snap-start"
              >
                <div class="animate-pulse">
                  <div
                    class="bg-gray-200 h-[260px] sm:h-[320px] lg:h-[381px] rounded-2xl mb-5"
                  />
                  <div class="space-y-3">
                    <div class="bg-gray-200 h-8 w-32 rounded-full" />
                    <div class="bg-gray-200 h-8 w-48 rounded" />
                    <div class="bg-gray-200 h-6 w-36 rounded" />
                  </div>
                </div>
              </div>
            </template>

            <!-- Package Cards -->
            <template v-else>
              <div class="contents" :key="activeCategory">
                <div
                  v-for="pkg in filteredPackages"
                  :key="pkg.id"
                  :class="[
                    'flex-shrink-0 w-[85%] sm:w-full md:w-[calc(50%-20px)] group cursor-pointer snap-start',
                    variant === 'classic'
                      ? 'lg:w-[521px]'
                      : 'lg:w-[calc(33.333%-27px)]',
                  ]"
                  data-card
                  @click="navigateToPackage(pkg)"
                >
                  <!-- Image -->
                  <div
                    class="relative h-[288px] sm:h-[320px] lg:h-[381px] rounded-2xl overflow-hidden mb-5"
                  >
                    <img
                      :src="pkg.imageUrl || '/images/hero-omra.jpg'"
                      :alt="pkg.title"
                      class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  <!-- Card Content -->
                  <div v-if="variant === 'classic'" class="flex flex-col gap-4">
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex items-center gap-3 flex-wrap">
                        <div
                          class="capitalizeinline-flex items-center gap-2 bg-[#FFF9EC] border border-[#D0A444] text-[#D0A444] px-3 py-1 rounded-[38px] text-[14px] font-semibold font-outfit leading-4"
                        >
                          <UiIconsAirplaneIcon class="w-5 h-5" />
                          {{ getFrequencyLabel(pkg) }}
                        </div>
                        <div
                          class="inline-flex items-center gap-2 bg-[#FFF9EC] border border-[#D0A444] text-[#D0A444] px-3 py-1 rounded-[38px] text-[14px] font-semibold font-outfit leading-4"
                        >
                          Min. {{ getMinTravelers(pkg) }} pers
                        </div>
                      </div>
                      <div class="flex flex-col items-end gap-1 flex-shrink-0">
                        <span class="text-[#6b7280] text-[13px] font-semibold">
                          à partir de
                        </span>
                        <div class="flex items-start gap-1">
                          <span
                            class="text-[#1f2937] text-[14px] font-medium font-outfit"
                            >€</span
                          >
                          <span
                            class="text-[#1f2937] text-[20px] font-bold font-outfit tracking-[-0.7px]"
                          >
                            {{
                              formatPrice(
                                pkg.pricing?.minPrice ??
                                  pkg.pricing?.weeklyMinPrice,
                              )
                            }}
                          </span>
                        </div>
                        <span class="text-[#6b7280] text-[13px] font-semibold">
                          /Par pèlerin
                        </span>
                      </div>
                    </div>
                    <div>
                      <h3
                        class="text-[24px] font-bold font-outfit text-[#1f2937] leading-8"
                      >
                        {{ pkg.title }}
                      </h3>
                      <p
                        class="text-[16px] font-semibold font-outfit text-[#636a73] tracking-[0.16px]"
                      >
                        {{ getDisplayDuration(pkg) }}
                      </p>
                    </div>
                  </div>
                  <div
                    v-else
                    class="flex items-start justify-between gap-4 mt-5"
                  >
                    <!-- Left Side: Badge, Title, Duration -->
                    <div class="flex flex-col gap-1.5 flex-1 min-w-0">
                      <!-- Departure Type Badges -->
                      <div class="flex flex-wrap gap-2">
                        <div
                          v-for="badge in getDepartureBadges(pkg)"
                          :key="badge"
                          class="capitalize inline-flex items-center gap-1.5 bg-primary-light border border-primary rounded-[38px] px-2 py-1 w-fit"
                        >
                          <UiIconsAirplaneIcon
                            class="w-5 h-5 text-primary flex-shrink-0"
                          />
                          <span
                            class="text-primary text-md font-semibold font-outfit whitespace-nowrap"
                          >
                            {{ badge }}
                          </span>
                        </div>
                      </div>

                      <!-- Title -->
                      <h3
                        class="text-[22px] sm:text-2xl lg:text-3xl font-bold font-outfit text-gray-800 leading-7 lg:leading-8 capitalize"
                      >
                        {{ pkg.title }}
                      </h3>

                      <!-- Duration -->
                      <p
                        class="text-sm lg:text-lg font-semibold font-outfit text-[#636a73] tracking-[0.16px]"
                      >
                        {{ getDisplayDuration(pkg) }}
                      </p>
                    </div>

                    <!-- Right Side: Pricing -->
                    <div class="flex flex-col items-end gap-1 flex-shrink-0">
                      <span class="text-gray-500 text-[13px] font-semibold">
                        <span class="capitalize">À</span> partir de
                      </span>
                      <div class="flex items-start gap-1">
                        <span
                          class="text-gray-800 text-md font-medium font-outfit leading-none mt-0.5"
                          >€</span
                        >
                        <span
                          class="text-gray-800 text-[22px] sm:text-xl lg:text-2xl font-bold font-outfit tracking-[-0.7px]"
                        >
                          {{
                            formatPrice(
                              pkg.pricing?.minPrice ??
                                pkg.pricing?.weeklyMinPrice,
                            )
                          }}
                        </span>
                      </div>
                      <span class="text-gray-500 text-[13px] font-semibold">
                        /Par pèlerin
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Empty State -->
            <div
              v-if="!loading && filteredPackages.length === 0"
              class="w-full text-center py-12"
            >
              <p class="text-gray-500 text-lg font-outfit">
                Aucun package disponible pour cette catégorie.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Package } from "~/types/package";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  watch,
} from "vue";
import IconRight from "../icons/IconRight.vue";

interface Props {
  packages: Package[];
  loading?: boolean;
  showHeader?: boolean;
  showTabs?: boolean;
  title?: string;
  initialCategory?: "omra" | "omra-ramadhan";
  variant?: "default" | "classic";
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  showHeader: true,
  showTabs: true,
  title: "",
  initialCategory: "omra",
  variant: "default",
});

const { showHeader, showTabs, title, variant } = toRefs(props);

const router = useRouter();

const categories = [
  { value: "omra", label: "Omra 2026" },
  { value: "omra-ramadhan", label: "Omra Ramadhan" },
];

const activeCategory = ref<string>(props.initialCategory);
const scrollContainer = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);
const animateCards = ref(false);
let animationTimeout: ReturnType<typeof setTimeout> | null = null;
const tabsEl = ref<HTMLElement | null>(null);
const tabRefs = ref<HTMLElement[]>([]);
const indicatorStyle = ref<Record<string, string>>({});
let tabsResizeObserver: ResizeObserver | null = null;

const filteredPackages = computed(() => {
  if (!props.packages || props.packages.length === 0) return [];

  return props.packages
    .filter((pkg) => {
      if (activeCategory.value === "omra-ramadhan") {
        return pkg.category === "omra-ramadhan" || pkg.isRamadan;
      }
      return (!pkg.category || pkg.category === "omra") && !pkg.isRamadan;
    })
    .sort((a, b) => {
      const priceA =
        a.pricing?.minPrice ?? a.pricing?.weeklyMinPrice ?? Infinity;
      const priceB =
        b.pricing?.minPrice ?? b.pricing?.weeklyMinPrice ?? Infinity;
      if (priceA !== priceB) return priceA - priceB;
      return a.title.localeCompare(b.title, "fr", { sensitivity: "base" });
    });
});

function formatDepartureDate(pkg: Package): string {
  if (pkg.fixedDepartureDate) {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(pkg.fixedDepartureDate));
  }

  if (pkg.availability?.from) {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(pkg.availability.from));
  }

  return "Dates flexibles";
}

function formatPrice(price: number | undefined): string {
  if (!price) return "—";
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

function navigateToPackage(pkg: Package) {
  const route =
    pkg.category === "omra-ramadhan" || pkg.isRamadan
      ? `/omra-ramadhan/${pkg.slug}`
      : `/omra/${pkg.slug}`;
  router.push(route);
}

function updateScrollState() {
  if (!scrollContainer.value) return;
  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
  const overflow = scrollWidth - clientWidth;
  canScrollLeft.value = scrollLeft > 1;
  canScrollRight.value =
    overflow > 1 && scrollLeft + clientWidth < scrollWidth - 1;
}

function triggerCardsAnimation() {
  animateCards.value = false;
  if (animationTimeout) {
    clearTimeout(animationTimeout);
  }
  requestAnimationFrame(() => {
    animateCards.value = true;
    animationTimeout = setTimeout(() => {
      animateCards.value = false;
    }, 400);
  });
}

function setTabRef(el: Element | ComponentPublicInstance | null) {
  if (!el) return;
  const htmlEl = el as HTMLElement;
  if (!tabRefs.value.includes(htmlEl)) {
    tabRefs.value.push(htmlEl);
  }
}

function updateIndicator() {
  if (!tabsEl.value) return;
  // Query directly from DOM to ensure we get the rendered element
  const activeEl = tabsEl.value.querySelector<HTMLElement>(
    `[data-tab="${activeCategory.value}"]`,
  );
  if (!activeEl) return;
  indicatorStyle.value = {
    width: `${activeEl.offsetWidth}px`,
    transform: `translate3d(${activeEl.offsetLeft}px, 0, 0)`,
  };
}

function getScrollAmount(): number {
  if (!scrollContainer.value) return 400;
  const firstCard =
    scrollContainer.value.querySelector<HTMLElement>("[data-card]");
  if (!firstCard) return 400;
  const styles = getComputedStyle(scrollContainer.value);
  const gap = parseFloat(styles.columnGap || styles.gap || "0");
  return firstCard.offsetWidth + gap;
}

function scrollLeft() {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollBy({
    left: -getScrollAmount(),
    behavior: "smooth",
  });
}

function scrollRight() {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollBy({
    left: getScrollAmount(),
    behavior: "smooth",
  });
}

const getMinTravelers = (pkg: Package) => {
  return pkg.minTravelersPerBooking || 1;
};

function getDisplayDuration(pkg: Package): string {
  // Ramadhan packages: use per-departure durationDays for range
  if (
    (pkg.isRamadan || pkg.category === "omra-ramadhan") &&
    Array.isArray(pkg.ramadanDepartures) &&
    pkg.ramadanDepartures.length > 0
  ) {
    const days = pkg.ramadanDepartures
      .map((d) => d.durationDays ?? null)
      .filter((d): d is number => d != null);
    if (days.length > 0) {
      const min = Math.min(...days);
      const max = Math.max(...days);
      const nightsMin = min - 1;
      const nightsMax = max - 1;
      return min === max
        ? `${nightsMin} Nuitées / ${min} Jours`
        : `${nightsMin} à ${nightsMax} Nuitées / ${min} à ${max} Jours`;
    }
    // Fall back to madinahNights + makkahNights
    if (pkg.madinahNights != null && pkg.makkahNights != null) {
      const nights = pkg.madinahNights + pkg.makkahNights;
      return `${nights} Nuitées / ${nights + 1} Jours`;
    }
  }
  return `${pkg.nights || 0} Nuitées / ${pkg.durationDays || 0} Jours`;
}

function formatShortDate(dateStr: string): string {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(dateStr));
}

function getDepartureBadges(pkg: Package): string[] {
  // Ramadhan packages: show departure dates
  if (pkg.isRamadan || pkg.category === "omra-ramadhan") {
    if (pkg.ramadanDepartures && pkg.ramadanDepartures.length > 0) {
      return pkg.ramadanDepartures.map((d) => formatShortDate(d.date));
    }
    if (pkg.fixedDepartureDate) {
      return [formatShortDate(String(pkg.fixedDepartureDate))];
    }
    return ["Dates flexibles"];
  }
  // Regular packages: show departure type
  if (pkg.inventoryRules && pkg.inventoryRules.length > 0) {
    const badges: string[] = [];
    const hasMonthly = pkg.inventoryRules.some(
      (rule) => rule.departureType === "MONTHLY_GROUP",
    );
    const hasWeekly = pkg.inventoryRules.some(
      (rule) =>
        rule.departureType === "WEEKLY_INDIVIDUAL" ||
        (rule.departureType == null && rule.type === "recurring-weekly"),
    );
    if (hasMonthly) badges.push("Mensuel");
    if (hasWeekly) badges.push("Hebdomadaire");
    if (badges.length > 0) return badges;
  }
  return [pkg.departureSchedule || pkg.frequency || "Hebdomadaire"];
}

const getFrequencyLabel = (pkg: Package) => {
  if (!pkg.inventoryRules?.length) {
    return "Hebdomadaire";
  }

  // Check if package has recurring weekly rules
  const hasRecurringWeekly = pkg.inventoryRules.some(
    (rule) => rule.type === "recurring-weekly",
  );

  return hasRecurringWeekly ? "Hebdomadaire" : "Départs spécifiques";
};

onMounted(() => {
  updateScrollState();
  if (props.showTabs) {
    // Wait for DOM layout before measuring tab positions
    nextTick(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateIndicator();
        });
      });
    });
    // ResizeObserver re-measures after fonts load (font-outfit changes button width)
    if (tabsEl.value) {
      tabsResizeObserver = new ResizeObserver(() => {
        updateIndicator();
      });
      tabsResizeObserver.observe(tabsEl.value);
    }
    window.addEventListener("resize", updateIndicator);
  }
  window.addEventListener("resize", updateScrollState);
});

onUnmounted(() => {
  if (animationTimeout) {
    clearTimeout(animationTimeout);
  }
  window.removeEventListener("resize", updateScrollState);
  if (props.showTabs) {
    window.removeEventListener("resize", updateIndicator);
    tabsResizeObserver?.disconnect();
  }
});

// Reset scroll position when category changes
watch(activeCategory, () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollTo({ left: 0, behavior: "smooth" });
  }
  triggerCardsAnimation();
  if (props.showTabs) {
    nextTick(() => updateIndicator());
  }
  nextTick(() => requestAnimationFrame(updateScrollState));
});

watch(filteredPackages, () => {
  nextTick(() => requestAnimationFrame(updateScrollState));
});

watch(
  () => props.packages,
  () => {
    if (props.showTabs) {
      nextTick(() => updateIndicator());
    }
  },
);
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Cards appearance animation on tab change */
.cards-animate [data-card] {
  animation: cards-in 0.4s ease both;
}

.cards-animate [data-card]:nth-child(2) {
  animation-delay: 40ms;
}

.cards-animate [data-card]:nth-child(3) {
  animation-delay: 80ms;
}

@keyframes cards-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
